import { Client } from '@stomp/stompjs';

export class StompClient {
    constructor({ url, username, password, heartbeat = 10000, autoReconnect = true, debug = false, getRetryInterval }) {
        this.url = url;
        this.username = username;
        this.password = password;
        this.heartbeat = heartbeat;
        this.autoReconnect = autoReconnect;
        this.debug = debug;
        this.getRetryInterval = getRetryInterval || ((n) => 1000 * n);
        this.retryCount = 0;
        this.connected = false;
        this.explicitDisconnect = false;
        this._subs = new Map();
        this._connectCbs = new Set();
        this._disconnectCbs = new Set();
        this._unhandledCbs = new Set();
        this._authErrorCbs = new Set();
    }

    connect() {
        return new Promise((resolve) => {
            this._initClient(resolve);
            this._client.activate();
        });
    }

    _initClient(onFirstConnect) {
        this._client = new Client({
            brokerURL: this.url,
            heartbeatOutgoing: this.heartbeat,
            heartbeatIncoming: this.heartbeat,
            // A getter, not a fixed object: switching to a session token has to
            // take effect on the next CONNECT without rebuilding the client.
            beforeConnect: () => {
                this._client.connectHeaders = { login: this.username, passcode: this.password };
            },
            connectHeaders: { login: this.username, passcode: this.password },
            onConnect: () => {
                this.connected = true;
                // Without this the backoff keeps growing across a long-lived
                // session, so a late drop waits minutes to retry.
                this.retryCount = 0;
                for (const cb of this._connectCbs) { try { cb(); } catch (e) {} }
                if (onFirstConnect) { const fn = onFirstConnect; onFirstConnect = null; fn(); }
            },
            onUnhandledMessage: (msg) => {
                for (const cb of this._unhandledCbs) { try { cb(msg); } catch (e) {} }
            },
            onStompError: (frame) => {
                if (this._isAuthError(frame)) {
                    this._handleAuthError(frame);
                    return;
                }
                this._handleDisconnect();
            },
            onWebSocketError: () => this._handleDisconnect(),
        });
    }

    // An expired or rejected token must not be retried on a loop: the broker
    // would refuse it identically every time. Callers are told instead, so they
    // can fetch a fresh token or fall back to the static credentials.
    _isAuthError(frame) {
        const headers = (frame && frame.headers) || {};
        const text = `${headers.message || ''} ${(frame && frame.body) || ''}`.toLowerCase();
        if (/access refused|not.?authori|unauthori|bad connect|invalid.*(token|credential)|expired/.test(text)) {
            return true;
        }
        // A STOMP-level error before we ever connected means the broker answered
        // and rejected our CONNECT; a broker that is simply unreachable surfaces
        // as a WebSocket error instead.
        return !this.connected;
    }

    _handleAuthError(frame) {
        const wasConnected = this.connected;
        this.connected = false;
        this._subs.clear();
        if (wasConnected) {
            for (const cb of this._disconnectCbs) { try { cb(); } catch (e) {} }
        }
        for (const cb of this._authErrorCbs) { try { cb(frame); } catch (e) {} }
    }

    // Swap credentials for the next CONNECT. Pass reconnect: true to apply them
    // immediately; otherwise they take effect on the next reconnection.
    setCredentials({ username, password }, { reconnect = false } = {}) {
        this.username = username;
        this.password = password;
        if (reconnect) this.reconnect();
    }

    reconnect() {
        if (!this._client) return;
        this.retryCount = 0;
        const wasConnected = this.connected;
        this.connected = false;
        this._subs.clear();
        if (wasConnected) {
            for (const cb of this._disconnectCbs) { try { cb(); } catch (e) {} }
        }
        // deactivate() resolves once the socket is really closed; reactivating
        // before that races and can leave two sockets open.
        Promise.resolve(this._client.deactivate())
            .then(() => { if (!this.explicitDisconnect) this._client.activate(); })
            .catch(() => {});
    }

    _handleDisconnect() {
        if (this.connected) {
            this.connected = false;
            this._subs.clear();
            for (const cb of this._disconnectCbs) { try { cb(); } catch (e) {} }
        }
        if (this.autoReconnect && !this.explicitDisconnect) {
            setTimeout(() => this._client && this._client.activate(), this.getRetryInterval(this.retryCount++));
        }
    }

    disconnect() {
        this.explicitDisconnect = true;
        if (this._client) this._client.deactivate && this._client.deactivate();
        this.connected = false;
        this._subs.clear();
        return Promise.resolve();
    }

    subscribe(topic, handler) {
        if (!this._client || !this.connected) {
            throw new Error('StompClient.subscribe: not connected');
        }
        const sub = this._client.subscribe(topic, (payload) => {
            let body;
            try { body = JSON.parse(payload.body); } catch (e) { body = payload.body; }
            handler(body, payload);
        });
        this._subs.set(topic, sub);
        return () => {
            const s = this._subs.get(topic);
            if (s) { s.unsubscribe(); this._subs.delete(topic); }
        };
    }

    publish(topic, body, headers = {}) {
        if (!this.connected) throw new Error('StompClient.publish: disconnected');
        const payload = typeof body === 'string' ? body : JSON.stringify(body);
        this._client.publish({ destination: topic, headers, body: payload });
    }

    onConnect(cb) { this._connectCbs.add(cb); return () => this._connectCbs.delete(cb); }
    onDisconnect(cb) { this._disconnectCbs.add(cb); return () => this._disconnectCbs.delete(cb); }
    onUnhandledMessage(cb) { this._unhandledCbs.add(cb); return () => this._unhandledCbs.delete(cb); }
    onAuthError(cb) { this._authErrorCbs.add(cb); return () => this._authErrorCbs.delete(cb); }
}
